import { MachineAssignmentPanel } from "../terminal-admin/MachineAssignmentPanel";
import { TerminalRepairDrawer } from "../terminal-admin/TerminalRepairDrawer";
import { HistoricalCorrectionDialog } from "./HistoricalCorrectionDialog";
import { AlertTriangle } from "lucide-react";
import { can } from "../../../shared/auth/access";
import { SupervisorWorkspace } from "../SupervisorWorkspace";
import { OperatorTerminalHeader as KioskHeader } from "../terminal/OperatorTerminalHeader";
import { TerminalState } from "../terminal/OperatorTerminalShell";
import { OperationalNotificationStrip } from "../terminal/OperationalNotificationStrip";
import { shiftHours } from "../production-time";
import { ProductionManagerHeader } from "./ProductionManagerHeader";
import { ProductionManagerLiveReport } from "./ProductionManagerLiveReport";
import { SupervisorSummaryDrawer } from "./SupervisorSummaryDrawer";
import { SupervisorDecisionSheet } from "./SupervisorDecisionSheet";
import { useSupervisorBoardWorkspace } from "./useSupervisorBoardWorkspace";
import type { SupervisorBoardOptions } from "./useSupervisorBoardWorkspace";

type Props = SupervisorBoardOptions & {
  onLogout: () => Promise<void>;
  onBack?: () => void;
};

export function SupervisorBoard({ user, notify, dataRefreshVersion, onLogout, onBack }: Props) {
  const workspace = useSupervisorBoardWorkspace({ user, notify, dataRefreshVersion });
  const { reference, runs, notifications, loading, updatedAt, loadError, load, terminalAdmin,
    activeShiftCode, terminalRepairLineId, setTerminalRepairLineId, setHistoricalCorrectionOpen,
    decisionTarget, directDecision, historicalCorrectionOpen, summaryOpen, shiftReport,
    openDecision, closeDecision, reviewRun, submitDecision, dataIsFresh } = workspace;
  const isProductionManager = user.role === "URETIM_YONETICISI";
  if (!isProductionManager && !reference) {
    return (
      <div className="production-supervisor-shell is-shift-supervisor production-supervisor-recovery">
        <KioskHeader
          label="Vardiya Amiri · Kontrol Merkezi"
          dataStatus={loading ? "loading" : "unavailable"}
          hours={shiftHours(activeShiftCode)}
          user={user}
          onLogout={onLogout}
        />
        <TerminalState
          title={loading ? "Vardiya verileri hazırlanıyor" : "Vardiya verileri alınamadı"}
          text={loading
            ? "Aktif vardiya, makine ve üretim bilgileri yükleniyor."
            : "Veri hizmetine şu anda ulaşılamıyor. Bu ekranda işlem yapılmadı; bağlantıyı yeniden deneyebilir veya önceki ekrana dönebilirsiniz."}
          actionLabel={loading ? undefined : "Yeniden dene"}
          actionDisabled={loading}
          onAction={() => void load()}
          secondaryActionLabel={onBack ? "Önceki ekrana dön" : undefined}
          onSecondaryAction={onBack}
          assistance="Sorun sürerse sistem yöneticisine vardiya ve kullanıcı bilginizi iletin."
        />
      </div>
    );
  }
  return (
    <div
      className={`production-supervisor-shell${isProductionManager ? " is-production-manager" : " is-shift-supervisor"}`}
    >
      {isProductionManager ? (
        <ProductionManagerHeader
          shiftCode={activeShiftCode}
          shiftHours={shiftHours(activeShiftCode)}
          user={user}
          onLogout={onLogout}
        />
      ) : null}
      <main className="production-supervisor-board">
        {terminalAdmin && reference && can(user, "production.assignment.manage") ? <MachineAssignmentPanel
          user={user} data={terminalAdmin} reference={reference} shiftCode={activeShiftCode}
          refreshVersion={dataRefreshVersion} open={workspace.assignmentOpen}
          onOpenChange={workspace.setAssignmentOpen} onSaved={workspace.refreshTerminalAdmin} notify={notify}
        /> : null}
        {!isProductionManager && reference ? <SupervisorWorkspace reference={reference} runs={runs} terminalAdmin={terminalAdmin} supervisorId={user.role === "VARDIYA_AMIRI" ? user.id : undefined} supervisorName={user.name} loading={loading} updatedAt={updatedAt} error={loadError} onRefresh={() => void load()} onBack={onBack} onLogout={onLogout} onManage={can(user, "production.run.manage") ? openDecision : undefined} onTerminalManage={can(user, "production.terminal.manage") ? setTerminalRepairLineId : undefined} onHistory={user.role === "VARDIYA_AMIRI" ? () => setHistoricalCorrectionOpen(true) : undefined} onReview={reviewRun} /> : null}
        {terminalRepairLineId && terminalAdmin && reference ? <TerminalRepairDrawer
          key={terminalRepairLineId} user={user} data={terminalAdmin} reference={reference}
          shiftCode={activeShiftCode} lineId={terminalRepairLineId} onClose={workspace.closeTerminalRepair}
          onSaved={workspace.refreshTerminalAdmin} notify={notify}
        /> : null}
        {isProductionManager ? <OperationalNotificationStrip items={notifications} mode="manager" /> : null}
        {reference?.source.warning && !/^SUNUM ORTAMI\s*[—–-]/u.test(reference.source.warning.trim()) ? (
          <div className="production-source-note">
            <AlertTriangle size={18} />
            <span>{reference.source.warning}</span>
          </div>
        ) : null}
        {user.role === "URETIM_YONETICISI" ? (
          <ProductionManagerLiveReport workspace={workspace} notify={notify} dataRefreshVersion={dataRefreshVersion} />
        ) : null}
        {user.role !== "URETIM_YONETICISI" ? (
          <>
            {decisionTarget && reference ? (
              <SupervisorDecisionSheet
                run={decisionTarget}
                directAction={directDecision}
                reference={reference}
                user={user}
                notify={notify}
                dataIsFresh={dataIsFresh}
                refreshData={() => { void load(); }}
                close={closeDecision}
                submit={submitDecision}
              />
            ) : null}
            {historicalCorrectionOpen ? <HistoricalCorrectionDialog
              user={user} notify={notify} close={() => workspace.setHistoricalCorrectionOpen(false)} onSaved={load}
            /> : null}
          </>
        ) : null}
        {summaryOpen &&
          can(user, "production.report.read") &&
          shiftReport.length > 0 ? (
          <SupervisorSummaryDrawer workspace={workspace} />
        ) : null}
      </main>
    </div>
  );
}
